import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CharterPersonKind } from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { CorporateCharterHttpService } from '@services/http/corporate-charter/corporate-charter-http.service';
import { TooltipModule } from 'primeng/tooltip';

/**
 * Documento de uma pessoa do contrato social. Vem sempre mascarado ("CPF ***.456.789-**"); quem tem a permissão
 * `DATAROOM_VIEW_PERSONAL_DOCUMENT` (`canReveal`, decidido pelo backend) vê o completo pelo olho, e o backend registra
 * o acesso em nome de quem clicou. CNPJ já vem completo (é dado público).
 */
@Component({
  selector: 'app-charter-person-document',
  imports: [TooltipModule],
  template: `
    @if (masked() || revealed()) {
      <span class="doc">
        <span class="doc__value">{{ prefix() }} {{ revealed() ?? masked() }}</span>
        @if (canReveal() && personId()) {
          <button
            type="button"
            class="doc__eye"
            [disabled]="loading()"
            [pTooltip]="revealed() ? 'Esconder' : 'Ver completo (acesso registrado em seu nome)'"
            tooltipPosition="top"
            [attr.aria-label]="revealed() ? 'Esconder documento' : 'Ver documento completo'"
            (click)="toggle()">
            <i
              class="pi"
              [class.pi-eye]="!revealed()"
              [class.pi-eye-slash]="!!revealed()"
              aria-hidden="true"></i>
          </button>
        }
      </span>
      @if (revealed()) {
        <small class="doc__notice">Acesso registrado em seu nome</small>
      }
    } @else {
      <span class="doc doc--empty">Não informado</span>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      flex-direction: column;
      gap: 2px;
    }

    .doc {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: #334155;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;

      &--empty {
        color: #94a3b8;
      }

      &__eye {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        padding: 0;
        border: 0;
        border-radius: 4px;
        background: none;
        color: #94a3b8;
        cursor: pointer;

        .pi {
          font-size: 0.8rem;
        }

        &:hover:not(:disabled) {
          background: #f1f5f9;
          color: #334155;
        }

        &:disabled {
          cursor: wait;
          opacity: 0.6;
        }
      }

      &__notice {
        color: #b45309;
        font-size: 0.68rem;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterPersonDocumentComponent {
  private readonly http = inject(CorporateCharterHttpService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly personId = input<string | null>(null);
  readonly masked = input<string | null>(null);
  readonly canReveal = input(false);
  readonly personKind = input<CharterPersonKind | null>(null);

  protected readonly revealed = signal<string | null>(null);
  protected readonly loading = signal(false);

  /** "CPF" ou "CNPJ" pelo tamanho do documento (ou pelo tipo de pessoa). */
  protected readonly prefix = computed(() => {
    const digits = (this.revealed() ?? this.masked() ?? '').replace(/[^\d*]/g, '');
    if (digits.length === 14 || this.personKind() === 'company' || this.personKind() === 'fund') {
      return 'CNPJ';
    }
    return 'CPF';
  });

  protected toggle(): void {
    if (this.revealed()) {
      this.revealed.set(null);
      return;
    }
    const personId = this.personId();
    if (!personId || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.http
      .revealPersonDocument(personId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.loading.set(false);
          this.revealed.set(result?.document ?? null);
        },
        error: error => {
          this.loading.set(false);
          this.formErrorHandler.showToastErrors(error);
        },
      });
  }
}
